import { useCallback } from 'react'
import nacl from 'tweetnacl'
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util'
import { useSignalClient } from './useSignalClient'

/**
 * Group Signal Protocol Hook
 * Implements Sender Key encryption with pairwise fan-out distribution.
 *
 * Flow:
 * 1. Generate a random 32-byte group session key per message
 * 2. Encrypt the message with this key (XSalsa20-Poly1305)
 * 3. Encrypt the session key for EACH member using 1:1 Signal Protocol
 * 4. Package as GroupPayload JSON
 *
 * This provides:
 * - Forward secrecy per message (new random key each time)
 * - Group membership control (only listed members can decrypt)
 * - Leverages 1:1 Double Ratchet for key distribution
 */

interface GroupPayload {
  v: number                     // Protocol version
  ct: string                    // Message ciphertext (base64)
  nonce: string                 // Message nonce (base64)
  sender_id: string             // Sender user ID
  dst: Record<string, string>   // memberId -> encrypted session key (base64)
}

export function useGroupSignalProtocol() {
  const signalClient = useSignalClient()

  /**
   * Encrypt a message for a group of members
   */
  const encryptGroupMessage = useCallback(async (
    text: string,
    memberIds: string[],
    currentUserId: string
  ): Promise<string> => {
    // 1. Generate random group session key (32 bytes)
    const sessionKey = nacl.randomBytes(32)
    const sessionKeyBase64 = encodeBase64(sessionKey)

    // 2. Encrypt message content with session key
    const msgNonce = nacl.randomBytes(24)
    const msgBytes = decodeUTF8(text)
    const msgCipher = nacl.secretbox(msgBytes, msgNonce, sessionKey)

    // 3. Encrypt session key for each member (fan-out)
    const destinations: Record<string, string> = {}

    await Promise.all(memberIds.map(async (memberId) => {
      if (memberId === currentUserId) return // Self handled separately below

      try {
        const encKey = await signalClient.encryptToUser(memberId, sessionKeyBase64, currentUserId)
        destinations[memberId] = encKey
      } catch (e) {
        console.error(`[GroupE2EE] Failed to encrypt key for ${memberId}:`, e)
      }
    }))

    // Self-copy for history decryption
    try {
      const selfKey = await signalClient.encryptToUser(currentUserId, sessionKeyBase64, currentUserId)
      destinations[currentUserId] = selfKey
    } catch (e) {
      console.error('[GroupE2EE] Failed to encrypt self-key:', e)
    }

    // 4. Construct payload
    const payload: GroupPayload = {
      v: 1,
      ct: encodeBase64(msgCipher),
      nonce: encodeBase64(msgNonce),
      sender_id: currentUserId,
      dst: destinations,
    }

    return JSON.stringify(payload)
  }, [signalClient])

  /**
   * Decrypt a group message
   */
  const decryptGroupMessage = useCallback(async (
    payloadStr: string,
    currentUserId: string
  ): Promise<string> => {
    try {
      // 1. Parse payload
      let payload: GroupPayload
      try {
        payload = JSON.parse(payloadStr)
      } catch (e) { console.warn("[GroupE2EE] parse failed:", e);
        return payloadStr // Not JSON, assume plaintext/legacy
      }

      if (!payload.v || !payload.dst || !payload.ct) {
        return payloadStr // Not a valid v1 group payload
      }

      // 2. Find my encrypted key
      const myKeyCipher = payload.dst[currentUserId]
      if (!myKeyCipher) {
        return '[GroupE2EE] Unreadable: No key for me'
      }

      // 3. Decrypt session key using 1:1 protocol with sender
      const sessionKeyBase64 = await signalClient.decryptFromUser(
        payload.sender_id, myKeyCipher, currentUserId
      )
      if (sessionKeyBase64.startsWith('[')) {
        return `[GroupE2EE] Key Decrypt Fail: ${sessionKeyBase64}`
      }

      // 4. Decrypt message content
      const sessionKey = decodeBase64(sessionKeyBase64)
      const nonce = decodeBase64(payload.nonce)
      const ciphertext = decodeBase64(payload.ct)

      const decrypted = nacl.secretbox.open(ciphertext, nonce, sessionKey)
      if (!decrypted) return '[GroupE2EE] Message Decrypt Failed'

      return encodeUTF8(decrypted)
    } catch (e) {
      console.error('[GroupE2EE] Decrypt Error:', e)
      return '[GroupE2EE] Error'
    }
  }, [signalClient])

  return {
    encryptGroupMessage,
    decryptGroupMessage,
  }
}
